// Admin → Integrations → Generic kind detail page.
//
// Handles any integration kind that has a `config_schema.fields` spec in
// `integration_kinds` and doesn't need a hand-crafted page. Currently used
// for: google_analytics, n8n, custom_http, openai, livekit — and any
// future kind added to the catalogue.
//
// Kind is derived from the current route path:
//   /admin/integrations/n8n              → kind='n8n'
//   /admin/integrations/google_analytics → kind='google_analytics'
//   /admin/integrations/openai           → kind='openai'
//   /admin/integrations/livekit          → kind='livekit'
//   /admin/integrations/custom_http      → kind='custom_http'
//
// allows_multiple=true  → instances list + "Add" flow
// allows_multiple=false → single config card shown inline

import { loadIntegrationCatalogue } from '../../integrations/index.js'
// resolveScope, GenericIntNewForm, GenericIntConfigField, and
// GenericIntInstanceCard all live in workspace/packages/shared and are
// auto-picked-up via sharedLibraries. saveIntegration/deleteIntegration/
// callCapability moved into the shared form file with the components.

// Derive the integration kind string from the last segment of the
// current path. The only special-case reversal needed: the hand-crafted
// pages own 'supabase', 'github', 'google', 'npm' — this page handles
// everything else as a 1:1 slug↔kind mapping.
const _kindFromRoot = (root) => {
  const path = root?.currentPath || ''
  const m = path.match(/^\/admin\/integrations\/([^?#/]+)/)
  return m ? m[1] : null
}

const _kindRow = (root) => {
  const kind = _kindFromRoot(root)
  if (!kind) return null
  return (root?._integrationHub?.kinds || []).find((k) => k.kind === kind) || null
}

// Human-readable overrides for kind slugs whose label needs brand casing
// that a simple title-case can't produce (OpenAI vs Openai, n8n vs N8N, etc.)
const _KIND_LABEL_OVERRIDES = {
  openai: 'OpenAI',
  livekit: 'LiveKit',
  n8n: 'n8n',
  custom_http: 'Custom HTTP',
  google_analytics: 'Google Analytics'
}

// Prefer the catalogue's `label` when loaded; fall back to a formatted
// slug so the browser tab + page header never show the raw `google_analytics`.
// If the catalogue's label is missing OR equals the slug (bad data), use
// the override map / title-case the slug instead.
const _kindLabel = (root) => {
  const kind = _kindFromRoot(root)
  const row = _kindRow(root)
  const rowLabel = row?.label
  if (rowLabel && rowLabel !== kind) return rowLabel
  if (!kind) return 'Integration'
  if (_KIND_LABEL_OVERRIDES[kind]) return _KIND_LABEL_OVERRIDES[kind]
  return kind.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}

const _instances = (root) => {
  const kind = _kindFromRoot(root)
  if (!kind) return []
  return (root?._integrationHub?.integrations || []).filter((i) => i.kind === kind)
}

// Fire once per path after the catalogue is loaded AND the install flag
// is in the URL. Guard keyed by kind slug so visiting `?install=1` for
// a different kind still triggers auto-open. The `_genericIntFormOpen`
// check prevents overwriting when the user already opened the form
// manually between navigation and catalogue load.
const _applyInstallParamIfReady = (root) => {
  if (typeof window === 'undefined') return
  const kind = _kindFromRoot(root)
  if (!kind || root._installAppliedFor === kind) return
  const params = new URLSearchParams(window.location.search)
  if (params.get('install') !== '1') return
  const row = _kindRow(root)
  if (!row) return
  if (root._genericIntFormOpen) {
    root._installAppliedFor = kind
    return
  }
  root.update({
    _installAppliedFor: kind,
    _genericIntFormOpen: true,
    [_draftKey]: {
      scopeType: params.get('scopeType') || row.default_scope || 'org',
      scopeId: params.get('scopeId') || null
    }
  })
}

const _loadInstances = async (root) => {
  const orgId = root?.jwtClaims?.workspace_org_id
  if (!orgId) {
    if (!root._genericIntWatcher) {
      const started = Date.now()
      root._genericIntWatcher = setInterval(() => {
        if (root?.jwtClaims?.workspace_org_id) {
          clearInterval(root._genericIntWatcher)
          root._genericIntWatcher = null
          _loadInstances(root)
        } else if (Date.now() - started > 30_000) {
          clearInterval(root._genericIntWatcher)
          root._genericIntWatcher = null
        }
      }, 1000)
    }
    return
  }
  const data = await loadIntegrationCatalogue(orgId)
  root.update({ _integrationHub: data })
  _applyInstallParamIfReady(root)
}

const _draftKey = '_genericIntDraft'

// ── Page root ────────────────────────────────────────────────────────────

export const adminIntegrationsGeneric = {
  extends: 'AppPage',
  metadata: (el, s) => ({
    title: _kindLabel(s.root) + ' / Integrations / Symbols Workspace'
  }),
  onInit: (el, s) => {
    if (el.call('shouldRedirectNonAdmin', s)) {
      const root = el.call('findRouteRoot', el)
      if (root) el.router('/', root, {}, { pushState: true })
    }
  },
  onCreate: (el, s) => _loadInstances(s.root),

  AdminSidebar: {},

  AdminMain: {
    extends: 'AppMain',
    padding: '0',

    Header: {
      extends: 'PageHeader',
      LeftGroup: {
        BackLink: {
          tag: 'button',
          type: 'button',
          padding: '0',
          background: 'transparent',
          border: 'none',
          color: 'caption',
          fontSize: 'Z2',
          cursor: 'pointer',
          text: '← Integrations',
          onClick: (e, el, s) => {
            const root = el.call('findRouteRoot', el)
            if (root) el.router('/admin/integrations', root)
          }
        },
        Title: {
          text: (el, s) => _kindLabel(s.root)
        }
      },
      RightGroup: {
        AddBtn: {
          tag: 'button',
          type: 'button',
          hide: (el, s) => {
            const kr = _kindRow(s.root)
            // Hide if single-slot and already configured
            if (!kr?.allows_multiple && _instances(s.root).length > 0) return true
            // Hide if form is already open
            return !!s.root?._genericIntFormOpen
          },
          padding: 'Z A',
          round: 'Y',
          background: 'blue',
          color: 'white',
          fontSize: 'Z2',
          fontWeight: '700',
          cursor: 'pointer',
          border: 'none',
          text: (el, s) => _kindRow(s.root)?.allows_multiple ? '+ Add another' : '+ Configure',
          onClick: (e, el, s) => {
            s.root.update({
              _genericIntFormOpen: true,
              [_draftKey]: {
                scopeType: _kindRow(s.root)?.default_scope || 'org',
                scopeId: null
              }
            })
          }
        }
      }
    },

    Body: {
      overflow: 'auto',
      padding: 'B',
      flow: 'y',
      gap: 'A',

      Hint: {
        tag: 'p',
        fontSize: 'Z2',
        color: 'caption',
        margin: '0',
        lineHeight: '1.5',
        text: (el, s) => _kindRow(s.root)?.description || ''
      },

      CapsBadges: {
        flow: 'x',
        gap: 'V',
        flexWrap: 'wrap',
        children: (el, s) => {
          const caps = _kindRow(s.root)?.capabilities || []
          return caps.map((c) => ({ cap: c }))
        },
        childrenAs: 'state',
        childExtends: {
          tag: 'span',
          padding: 'V Z',
          round: 'Y',
          fontSize: 'Y1',
          fontWeight: '700',
          background: 'overlayStrong',
          color: 'caption',
          text: (el, s) => s.cap || ''
        }
      },

      GenericIntNewForm: {},

      SingleSlotHint: {
        hide: (el, s) => {
          const kr = _kindRow(s.root)
          if (!kr) return true
          // Show only for single-slot with no instances and form closed
          return kr.allows_multiple || _instances(s.root).length > 0 || !!s.root?._genericIntFormOpen
        },
        tag: 'p',
        fontSize: 'Z2',
        color: 'caption',
        margin: '0',
        text: (el, s) => {
          const kr = _kindRow(s.root)
          return `No ${kr?.label || 'integration'} configured yet. Click "+ Configure" to set it up.`
        }
      },

      InstanceList: {
        flow: 'y',
        gap: 'Z1',
        hide: (el, s) => _instances(s.root).length === 0,
        childExtends: 'GenericIntInstanceCard',
        childrenAs: 'state',
        children: (el, s) => _instances(s.root)
      }
    }
  }
}
